import { ProgressCard } from "../common/ProgressCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { AlertTriangle, CloudUpload, MapPin, Table2, NotebookText } from "lucide-react";
import { InfoCallout } from "../common/InfoCallout";
import { useWizard } from "@/contexts/WizardContext";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { UploadSectionIcon } from "../common/UploadSectionIcon";
import { LockedBanner } from "../common/LockedBanner";
import { CardPanelHeader } from "../common/CardPanelHeader";
import { SectionCard } from "../common/SectionCard";
import { SectionHeader } from "../common/SectionHeader";
import { TransitionMeasuresBlock } from "@/components/workflow/common/TransitionMeasuresBlock";

const FIELD_LABEL_CLASS = "text-sm font-semibold leading-5 text-slate-900";
const FIELD_INPUT_CLASS =
  "h-11 rounded-2xl border-zinc-200 bg-slate-50 px-4 text-base text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300";

function SitesIcon() {
  return <MapPin className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function LandUseIcon() {
  return <Table2 className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}

function NotesIcon() {
  return <NotebookText className="h-5 w-5 shrink-0 text-primary" strokeWidth={1.8} />;
}


function UploadCloudIcon() {
  return <CloudUpload className="h-8 w-8 shrink-0 text-primary" strokeWidth={1.5} />;
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  min,
  max,
  step,
  hasError,
  hint,
}: {
  label: string;
  value: number | null;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  required?: boolean;
  min?: string;
  max?: number;
  step?: string;
  hasError?: boolean;
  hint?: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className={FIELD_LABEL_CLASS}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      <Input
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`${FIELD_INPUT_CLASS} ${hasError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
      />
      {hint && <p className="text-xs leading-4 text-slate-400">{hint}</p>}
    </div>
  );
}

export function BiodiversityCard({ onClose }: { onClose: () => void }) {
  const {
    biodiversityData,
    updateBiodiversityData,
    biodiversityCompletion,
    forceSave,
    saveStatus,
    biodiversitySupportingDocuments,
    setBiodiversitySupportingDocuments,
    isLocked,
    isReadOnly,
    assessmentId,
  } = useWizard();
  const disabled = isLocked || isReadOnly;

  const [numberOfSites, setNumberOfSites] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!assessmentId) return;
    fetch(`/api/assessments/${assessmentId}/general/number-of-sites`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setNumberOfSites(data.numberOfSites ?? null))
      .catch(() => setNumberOfSites(null));
  }, [assessmentId]);

  const referenceSiteCount = typeof numberOfSites === "string"
    ? (numberOfSites === "5+" ? 5 : parseInt(numberOfSites, 10))
    : null;
  const maxSites = typeof numberOfSites === "string" && numberOfSites !== "5+"
    ? parseInt(numberOfSites, 10)
    : null;
  const sitesNotConfigured = numberOfSites === null;

  const [exceedsError, setExceedsError] = useState(false);
  const saveBlocked = sitesNotConfigured || exceedsError;

  useEffect(() => {
    if (referenceSiteCount === null || Number.isNaN(referenceSiteCount)) return;
    if (biodiversityData.sitesInSensitiveAreas !== null) return;

    updateBiodiversityData({ sitesInSensitiveAreas: referenceSiteCount });
    setExceedsError(false);
  }, [referenceSiteCount, biodiversityData.sitesInSensitiveAreas, updateBiodiversityData]);

  const handleNumberChange = (field: keyof typeof biodiversityData, value: string) => {
    const numValue = value.trim() === "" ? null : parseFloat(value);
    const cleanValue = Number.isNaN(numValue as number) ? null : numValue;

    if (field === "sitesInSensitiveAreas" && maxSites !== null && cleanValue !== null && cleanValue > maxSites) {
      setExceedsError(true);
      return;
    }

    if (field === "sitesInSensitiveAreas") {
      setExceedsError(false);
    }

    updateBiodiversityData({ [field]: cleanValue });
  };

  const handleSave = () => {
    forceSave();
  };

  return (
    <div className="flex flex-col gap-6">
      <CardPanelHeader
        title={"Biodiversity (B5)"}
        description={"Tell us about your sites and whether any are located close to protected natural areas, forests, wetlands, or wildlife habitats"}
        onClose={onClose}
        onSave={handleSave}
        saveStatus={saveStatus}
        disabled={disabled || saveBlocked}
        tag={"Environment"}
      />

      {disabled && <LockedBanner />}

      {sitesNotConfigured && (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {"Number of company sites is not set. Please fill it in the"}
            <strong className="ml-1">{"General tab"}</strong>
            {"before saving."}
          </p>
        </div>
      )}

      {exceedsError && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            {`Sites in sensitive areas (${biodiversityData.sitesInSensitiveAreas}) cannot exceed the total number of company sites (${maxSites}) set in General tab. Value not accepted.`}
          </p>
        </div>
      )}

      <SectionCard  header={<SectionHeader icon={<SitesIcon />} title={"1) Sites Near Biodiversity-Sensitive Areas"} />}>
        <div className="flex flex-col gap-4">
          <NumberField
            label={"Number of sites in/near sensitive area"}
            value={biodiversityData.sitesInSensitiveAreas}
            onChange={(value) => handleNumberChange("sitesInSensitiveAreas", value)}
            placeholder={"e.g. 2"}
            min="0"
            max={maxSites ?? undefined}
            disabled={disabled}
            required
            hasError={exceedsError}
            hint={"Count of sites (required)"}
          />
          <NumberField
            label={"Total area of those sites (hectares)"}
            value={biodiversityData.areaOfSensitiveSites}
            onChange={(value) => handleNumberChange("areaOfSensitiveSites", value)}
            placeholder={"e.g. 5.5"}
            min="0"
            step="0.1"
            disabled={disabled}
            hint={"Area in ha (optional)"}
          />
        </div>
        <InfoCallout
          label={`${"Tip:"}:`}
          text={"Use your site register, property leases, or maps as evidence"}
        />
      </SectionCard>

      <SectionCard  header={<SectionHeader icon={<LandUseIcon />} title={"2) Land-Use Metrics"} />}>
        <div className="flex flex-col gap-4">
          <NumberField
            label={"Total land use (hectares)"}
            value={biodiversityData.totalLandUse}
            onChange={(value) => handleNumberChange("totalLandUse", value)}
            placeholder={"e.g. 10"}
            min="0"
            step="0.1"
            disabled={disabled}
            required
            hint={"All land owned/managed (required)"}
          />
          <NumberField
            label={"How much of your land is covered by buildings, roads, or hard surfaces? (optional) "}
            value={biodiversityData.sealedArea}
            onChange={(value) => handleNumberChange("sealedArea", value)}
            placeholder={"e.g. 2.5"}
            min="0"
            step="0.1"
            disabled={disabled}
            hint={"Buildings, roads, concrete (optional)"}
          />
          <NumberField
            label={"How much of your land is left as natural vegetation, trees, or green space? (optional)"}
            value={biodiversityData.natureOrientedOnSite}
            onChange={(value) => handleNumberChange("natureOrientedOnSite", value)}
            placeholder={"e.g. 3"}
            min="0"
            step="0.1"
            disabled={disabled}
            hint={"On your premises (optional)"}
          />
          <NumberField
            label={"Nature-oriented area off-site (hectares)"}
            value={biodiversityData.natureOrientedOffSite}
            onChange={(value) => handleNumberChange("natureOrientedOffSite", value)}
            placeholder={"e.g. 1.5"}
            min="0"
            step="0.1"
            disabled={disabled}
            hint={"Restoration projects, partnerships (optional)"}
          />
        </div>
        <InfoCallout
          label={`${"Note:"}:`}
          text={"Total land use is required. Other fields are optional but help demonstrate biodiversity stewardship."}
        />
      </SectionCard>

      <SectionCard  header={<SectionHeader icon={<NotesIcon />} title={"3) Additional Notes (Optional)"} />}>
        <Textarea
          placeholder={"e.g. Native tree planting program, protected wetland area, wildlife corridor maintenance..."}
          className="min-h-[140px] rounded-2xl border-zinc-200 bg-slate-50 px-4 py-3 text-base leading-6 text-slate-900 shadow-none placeholder:text-gray-500 focus-visible:ring-slate-300"
          value={biodiversityData.notes}
          onChange={(e) => updateBiodiversityData({ notes: e.target.value })}
          disabled={disabled}
        />
      </SectionCard>

      <SectionCard  header={<SectionHeader icon={<UploadSectionIcon />} title={"Supporting Documents"} />}>
        <DocumentUploadSection
          fieldIdentifier="environment-biodiversity"
          documents={biodiversitySupportingDocuments}
          onDocumentsChange={setBiodiversitySupportingDocuments}
          currentTab="environment"
          label={"Supporting Documents"}
          description={"Upload site maps, property leases, or environmental assessments"}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
          maxFiles={10}
          borderColor="border-slate-200"
          hideHeader
          className="rounded-[24px] border-2 border-slate-200 bg-slate-50 p-4 sm:p-10"
          emptyStateButtonClassName="rounded-[24px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
          documentsListClassName="space-y-3"
          itemClassName="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between"
          actionButtonClassName="rounded-3xl border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          emptyStateContent={
            <div className="flex flex-col items-center text-center">
              <div className="pb-4">
                <div className="rounded-full bg-slate-500/5 p-4">
                  <UploadCloudIcon />
                </div>
              </div>
              <p className="text-base font-bold leading-6 text-slate-800">
                Click to upload or drag and drop
              </p>
              <p className="pt-1 text-sm leading-5 text-slate-500">
                {"Upload site maps, property leases, or environmental assessments"}
              </p>
            </div>
          }
        />
      </SectionCard>

      <TransitionMeasuresBlock topicId="biodiversity" currentTab="environment" disabled={disabled} />

      <ProgressCard completion={biodiversityCompletion} />
    </div>
  );
}
